import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import { SaveDataObj } from "../../../../framework/saveData";
import { infiniteListPageSize } from "../../../../models/common/webConsts";
import * as appReportService from "../../../../models/services/appReportService";
import { ISessionState } from "../../../../models/session/sessionState";
import { ActionContainer } from "../../../framework/ActionContainer";
import { DialogSettingsCard } from "../../../framework/Container/DialogSettingsCard";
import { List } from "../../../framework/List/List";
import { ListIconButtonsItem } from "../../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListLinkValue } from "../../../framework/List/ListLinkValue";
import { ListSearchFilter } from "../../../framework/List/ListSearchFilter";
import { Box, Typography } from "@mui/material";
import { ListStateIconSuccess } from "source/components/framework/List/ListStateIconSuccess";
import { IListCardData } from "source/components/framework/List/ListLine";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { IAppReportTemplateListItem } from "ui/Scripts/source/models/appReportTemplate/appReportTemplateListItem";
import { useEffect } from "react";
import { AppReportTemplateDialog } from "../../AppReportTemplateDialog";
import { AppReportTemplateNewDialog } from "../../AppReportTemplateNewDialog";

export interface IAppReportTemplateGridProp {
    companyId: string;
    sessionState: ISessionState;
}

interface IAppReportTemplateGridState {
    // List
    intialized: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: IAppReportTemplateListItem[];
    selectedId: string;
    checkedIds: string[];

    // New Form Dialog
    showNewFormDialog: boolean;

    // Edit Dialog
    showEditDialog: boolean;
    editDialogId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
    editDialogAddToCompany: boolean;
}

export const AppReportTemplateGrid = (props: IAppReportTemplateGridProp) => {
    const [state, setState] = React.useState<IAppReportTemplateGridState>({
        intialized: false,
        isLoading: false,
        page: 1,
        hasMore: true,
        filter: "",
        pageSize: infiniteListPageSize,
        sortColumn: "activeState",
        sortOrderIsAsc: false,
        items: [],
        selectedId: null,
        checkedIds: [],
        showNewFormDialog: false,
        showEditDialog: false,
        editDialogId: "",
        editDialogHasPrevious: false,
        editDialogHasNext: false,
        editDialogAddToCompany: false
    });
    
    const searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string,
        sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, moveNext: boolean = false,
        setEditDialogData: boolean = true) => {
        setState((old) => ({ ...old, isLoading: true }));
        const result = await AppUtils.callService(() => appReportService.getAppReportTemplateListItems(props.companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc, null), false, false, () => setState((old) => ({ ...old, isLoading: false })));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        const editDialogStateChange = setEditDialogData
            ? {
                editDialogId: moveNext && selectedItem ? selectedItem.id : "",
                editDialogHasPrevious: index > 0,
                editDialogHasNext: index < items.items.length - 1 || result.hasMore,
                editDialogAddToCompany: false
            }
            : null;
        setState((old) => ({
            ...old,
            ...editDialogStateChange,
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
        }));
    };

    const refreshList = async (setEducationDialogData: boolean = true) => {
        await searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true, false, setEducationDialogData);
    };

    useEffect(() => {
        searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
    }, []);

    useEffect(() => {
        if (!state.intialized) return;
        searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }, [props.companyId ?? ""]);

    const changeFilter = (filter: string) => {
        searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    const handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        setState((old) => ({
            ...old,
            selectedId,
            checkedIds
        }));
    };

    //#region Edit Dialog
    const handleShowEditDialog = (appReportTemplateId: string, addToCompany: boolean) => {
        const isNew = appReportTemplateId === Base.emptyGuid || addToCompany;
        const index = isNew ? 0 : state.items.findIndex(i => i.id === appReportTemplateId);
        if (index < 0) return;
        setState((old) => ({
            ...old,
            showNewFormDialog: false,
            showEditDialog: true,
            editDialogId: appReportTemplateId,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: isNew ? false : index < state.items.length - 1 || state.hasMore,
            editDialogAddToCompany: addToCompany
        }));
    };

    const handleAddNew = () => {
        handleShowEditDialog(Base.emptyGuid, false);
    };

    const handleShowNewFormDialogOpen = () => {
        setState((old) => ({
            ...old,
            showNewFormDialog: true,
        }));
    };

    const handleShowNewFormDialogClose = () => {
        setState((old) => ({
            ...old,
            showNewFormDialog: false,
        }));
    };

    const handleOpenLine = (id: string) => {
        handleShowEditDialog(id, false);
    };

    const handleEditDialogOk = async (savedAppReportTemplateId: string) => {
        refreshList(false);
    };

    const handleEditDialogCancel = () => {
        setState((old) => ({
            ...old,
            showEditDialog: false,
        }));
        refreshList(false);
    };
    //#endregion Edit Dialog

    const getRemoveSaveData = (props: IAppReportTemplateGridProp, ids: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("ids", ids);
        return result;
    };

    const remove = async (ids: string[]): Promise<ApiSuccess> => {
        if (ids.length < 1) return;
        const saveData = getRemoveSaveData(props, ids);
        // Call server
        const result = await AppUtils.callService(() => appReportService.removeAppReportTemplates(saveData.obj));
        if (!result) return null;
        refreshList();
        return result;
    };

    const handleRemove = async () => {
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        const selectedItems = state.items.filter(i => selectedIds.indexOf(i.id) > -1);
        if (selectedItems.length < 1) return;
        if (await AppUtils.showConfirmationDialog(selectedItems.length > 1
            ? Translations.DoYouReallyWantToRemoveSelectedReportTemplates
            : String.format(Translations.ReportTemplateRemoveConfirmation, selectedItems[0].code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            // Call server
            remove(selectedIds);
        }
    };

    const setEditDialogItemByIndex = (index: number) => {
        if (index < 0) return;
        if (index >= state.items.length) {
            if (!state.hasMore) return;
            searchItems(state.pageSize, state.page + 1, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false, true);
        } else {
            setState((old) => ({
                ...old,
                selectedId: state.items[index].id,
                editDialogId: state.items[index].id,
                editDialogHasPrevious: index > 0,
                editDialogHasNext: index < state.items.length - 1 || state.hasMore,
            }));
        }
    };

    const getItemCardData = (item: IAppReportTemplateListItem): IListCardData => {
        return {
            sm: 4,
            rows: [
                {
                    content:
                        <Box sx={{ marginBottom: "-10px", width: "100%" }}>
                            <Box display="flex" justifyContent="space-between" width="100%">
                                <ListLinkValue onClick={() => handleOpenLine(item.id)}>{item.code}</ListLinkValue>
                                {(item.activeState > 0) &&
                                    <Box sx={{ marginRight: "-8px", display: "flex" }} >
                                        <Box sx={{ marginLeft: "10px" }} >
                                            <ListStateIconSuccess tooltip={Translations.InUse} />
                                        </Box>
                                    </Box>
                                }
                            </Box>
                            <Box sx={{ height: "40px", overflow: "hidden", textOverflow: "ellipsis" }} title={item.comment}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>{item.comment}</Typography>
                            </Box>
                        </Box>
                }
            ],
            id: item.id
        };
    };

    const getItemCardDatas = (): IListCardData[] => {
        const result = state.items.filter(i => i.activeState).map(i => getItemCardData(i));
        const notInUseItems = state.items.filter(i => !i.activeState);
        if (notInUseItems.length > 0) {
            result.push({ id: Translations.NotInUse, subTitle: Translations.NotInUse, rows: [] });
            result.push(...notInUseItems.map(i => getItemCardData(i)));
        }
        return result;
    };

    return (
        <>
            <DialogSettingsCard
                fitToContainerHeight
                title={props.companyId ? Translations.ReportTemplates : Translations.DefaultReportTemplates}
                info={props.companyId ? Translations.AppReportTemplateModuleInfoCompanyReportTemplates : null}
                cardContent={
                    <ActionContainer
                        leftItems={
                            <ListSearchFilter
                                searchFilter={state.filter}
                                onSearchClick={changeFilter}
                            />
                        }
                        rightItems={
                            <ListIconButtonsItem
                                addTooltip={Translations.AddTaskType}
                                removeTooltip={Translations.RemoveTaskType}
                                removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                onAdd={!props.companyId ? handleAddNew : handleShowNewFormDialogOpen}
                                onRemove={handleRemove}
                            />
                        }
                    />
                }
                content={
                    <List
                        intialized={state.intialized}
                        isLoading={state.isLoading}
                        hasMore={state.hasMore}
                        multiSelect={false}
                        globalLoadingInUse={true}
                        noDataMessage={props.companyId ? Translations.AppReportTemplateModuleInfoCompanyReportTemplatesNotFound : undefined}
                        height="100%"
                        cards={getItemCardDatas()}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={handleSetSelectedIds}
                        onSearchLines={handleSearchLines}
                        onOpenLine={handleOpenLine}
                    />
                }
            />
            {state.showEditDialog && (
                <AppReportTemplateDialog
                    id={state.editDialogId}
                    sessionState={props.sessionState}
                    companyId={props.companyId}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    addToCompany={state.editDialogAddToCompany}
                    onSaved={handleEditDialogOk}
                    onClose={handleEditDialogCancel}
                    onRemove={remove}
                    onRefreshList={() => refreshList(false)}
                    onPrevious={() => List.moveToPrevious(state, setEditDialogItemByIndex)}
                    onNext={() => List.moveToNext(state, setEditDialogItemByIndex)}
                />
            )}
            {state.showNewFormDialog && (
                <AppReportTemplateNewDialog
                    companyId={props.companyId}
                    onOk={(appReportTemplateId: string) => handleShowEditDialog(appReportTemplateId, true)}
                    onCancel={handleShowNewFormDialogClose}
                />
            )}
        </>
    );
};
